import { TooltipDirective } from '../../../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../../../componentes/botones';
import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import * as Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx-js-style';
import * as FileSaver from 'file-saver';
import {
  IFilter,
  IQueryParam,
  IReporteGenerico,
  TipoDispositivoLorawan,
  TipoValoresReporte,
} from 'modelos/src';
import {
  DatePickerComponent,
  DateRangeValue,
} from '../../../../../../../componentes/date-picker/date-picker.component';
import { Lista2Service } from '../../../../../../../auxiliares/servicios/lista2.service';
import { ChartColors } from '../../../../../../../auxiliares/servicios/chart-colors.service';
import { ILuminariaExtendida } from '../../../../luminarias.types';
import { CardHeaderComponent } from '../../../../../../../componentes/card-header/card-header.component';

export interface GraficoExpandidoData {
  luminaria: ILuminariaExtendida;
  key: string;
  titulo: string;
  tipo: TipoDispositivoLorawan;
  colorMap: Record<string, string>;
  yLabels: Record<string, string>;
}

@Component({
  selector: 'app-grafico-expandido-dialog',
  standalone: true,
  imports: [TooltipDirective, 
    CommonModule,
    FormsModule,
    MatCardModule,
    BOTONES,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    
    MatTabsModule,
    HighchartsChartModule,
    DatePickerComponent,
    CardHeaderComponent,
  ],
  templateUrl: './grafico-expandido-dialog.component.html',
  styleUrl: './grafico-expandido-dialog.component.scss',
})
export class GraficoExpandidoDialogComponent implements OnInit {
  private lista2 = inject(Lista2Service);
  readonly dialogRef = inject(MatDialogRef<GraficoExpandidoDialogComponent>);
  readonly data: GraficoExpandidoData = inject(MAT_DIALOG_DATA);

  Highcharts = Highcharts;
  chartOptions?: Highcharts.Options;
  cargando = false;
  updateFlag = false;
  serieData: [number, number][] = [];
  reportesCacheados: IReporteGenerico[] = [];

  hoy = new Date();
  maxEjeY: number | null = null;

  selectedKey: string = this.data.key;

  readonly keysMap: Record<string, string[]> = {
    'Luminaria Wellness': ['voltage', 'current', 'temperature', 'lumenes'],
    'Luminaria GPE': ['corriente', 'voltaje', 'potencia', 'energiaTotal'],
    'Luminaria ACTIS FING': ['corriente', 'voltaje', 'potencia'],
  };

  readonly seriesNamesMap: Record<string, Record<string, string>> = {
    'Luminaria Wellness': {
      voltage: 'Voltaje',
      current: 'Corriente',
      temperature: 'Temperatura',
      lumenes: 'Lúmenes',
    },
    'Luminaria GPE': {
      corriente: 'Corriente (mA)',
      voltaje: 'Voltaje (V)',
      potencia: 'Potencia (W)',
      energiaTotal: 'Energía (kWh)',
    },
    'Luminaria ACTIS FING': {
      corriente: 'Corriente (mA)',
      voltaje: 'Voltaje (V)',
      potencia: 'Potencia (W)',
    },
  };

  get allKeys(): string[] {
    return this.keysMap[this.data.tipo] ?? [this.data.key];
  }

  get allSeriesNames(): Record<string, string> {
    return (
      this.seriesNamesMap[this.data.tipo] ?? {
        [this.data.key]: this.data.titulo,
      }
    );
  }

  get tituloActual(): string {
    return this.allSeriesNames[this.selectedKey] ?? this.selectedKey;
  }

  // Rango por defecto: último día
  rango: DateRangeValue = (() => {
    const fin = new Date();
    const inicio = new Date(fin.getTime() - 24 * 60 * 60 * 1000);
    return { start: inicio.toISOString(), end: fin.toISOString() };
  })();

  async ngOnInit() {
    await this.cargarDatos();
  }

  async onRangoChange(rango: DateRangeValue) {
    if (!rango?.start || !rango?.end) return;
    this.rango = rango;
    await this.cargarDatos();
  }

  async cambiarKey(key: string) {
    if (key === this.selectedKey) return;
    this.selectedKey = key;
    this.maxEjeY = null;
    this.buildChart(); // solo rearma el gráfico, sin fetch
  }

  async cargarDatos() {
    if (!this.data?.luminaria?._id) return;

    this.cargando = true;
    this.chartOptions = undefined;

    const { luminaria, tipo } = this.data;

    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: luminaria._id,
    } as any;

    if (this.rango?.start && this.rango?.end) {
      (filter as any)['valores.timestamp'] = {
        $gte: this.rango.start,
        $lte: this.rango.end,
      };
    }

    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'valores.timestamp',
      limit: 1000,
      includeChildren: true,
    };

    let tipoReporte: TipoValoresReporte;
    if (tipo === 'Luminaria Wellness') {
      tipoReporte = 'Luminaria Wellness';
    } else if (tipo === 'Luminaria GPE') {
      tipoReporte = 'Luminaria GPE Energía';
    } else {
      tipoReporte = 'Luminaria ACTIS FING Energía';
    }

    try {
      const respuesta = await firstValueFrom(
        this.lista2.reportesGenericos([tipoReporte], query),
      );
      this.reportesCacheados = respuesta.datos;
      this.buildChart();
    } catch (error) {
      console.error('Error cargando datos del gráfico expandido:', error);
    } finally {
      this.cargando = false;
    }
  }

  private buildChart() {
    const key = this.selectedKey;
    const titulo = this.tituloActual;
    const { colorMap, yLabels } = this.data;

    const serieData: [number, number][] = [];
    this.reportesCacheados.forEach((reporte) => {
      if (!reporte?.valores?.timestamp) return;
      const time = new Date(reporte.valores.timestamp).getTime();
      if (isNaN(time)) return;
      const valor = (reporte.valores as any)[key];
      if (typeof valor === 'number') serieData.push([time, valor]);
    });
    this.serieData = serieData;

    const backgroundColor = ChartColors.backgroundColor();
    const textColor = ChartColors.textColor();
    const gridColor = ChartColors.gridColor();
    const axisLabelColor = ChartColors.axisLabelColor();

    this.chartOptions = {
      chart: { type: 'line', height: 450, animation: false, backgroundColor },
      title: { text: undefined },
      time: { useUTC: false },
      accessibility: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        type: 'datetime',
        title: { text: 'Fecha', style: { color: axisLabelColor } },
        labels: {
          style: { color: axisLabelColor },
          formatter: function (
            this: Highcharts.AxisLabelsFormatterContextObject,
          ) {
            const interval = (this.axis as any).tickInterval;
            if (interval >= 28 * 24 * 3600 * 1000) {
              return Highcharts.dateFormat('%m/%Y', this.value as number);
            } else if (interval >= 24 * 3600 * 1000) {
              return Highcharts.dateFormat('%d/%m/%Y', this.value as number);
            } else {
              return Highcharts.dateFormat('%d/%m %H:%M', this.value as number);
            }
          },
        },
        crosshair: { width: 1 },
        gridLineColor: gridColor,
        lineColor: gridColor,
        tickColor: gridColor,
      },
      yAxis: {
        title: { text: yLabels[key], style: { color: axisLabelColor } },
        labels: { style: { color: axisLabelColor } },
        gridLineColor: gridColor,
        max: this.maxEjeY ?? undefined,
      },
      tooltip: {
        shared: false,
        animation: false,
        backgroundColor,
        style: { color: textColor },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const fecha = Highcharts.dateFormat(
            '%d/%m/%Y %H:%M:%S',
            this.x as number,
          );
          return `<b>${fecha}</b><br/>${this.series.name}: <b>${this.y}</b>`;
        },
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          animation: false,
          marker: {
            enabled: serieData.length < 50,
            radius: 3,
            states: { hover: { enabled: true, radius: 5 } },
          },
        },
      },
      series: [
        {
          type: 'line',
          name: titulo,
          data: serieData,
          color: colorMap[key],
          lineWidth: 2,
        },
      ],
    };

    this.updateFlag = !this.updateFlag;
  }

  aplicarMaxEjeY() {
    this.buildChart();
  }

  exportarExcel() {
    if (!this.reportesCacheados.length) return;

    const keys = this.allKeys;
    const nombres = this.allSeriesNames;
    const yLabels = this.data.yLabels;

    const filas = this.reportesCacheados
      .filter((r) => r?.valores?.timestamp)
      .map((r) => {
        const valores = r.valores as any;
        const fila: Record<string, any> = {
          Fecha: new Date(valores.timestamp)
            .toLocaleString('es-AR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false,
            })
            .replace(',', ''),
        };
        keys.forEach((key) => {
          const nombre = nombres[key] ?? key;
          const unidad = yLabels[key];
          const columna = unidad ? `${nombre} (${unidad})` : nombre;
          fila[columna] = typeof valores[key] === 'number' ? valores[key] : '';
        });
        return fila;
      });

    const ws = XLSX.utils.json_to_sheet(filas);
    this._autoFitColumns(ws);
    this._applyHeaderStyle(ws);

    const buffer = XLSX.write(
      { Sheets: { Datos: ws }, SheetNames: ['Datos'] },
      { bookType: 'xlsx', type: 'array', cellStyles: true },
    );
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const nombreLuminaria = (this.data.luminaria as any)?.nombre ?? 'luminaria';
    const fechaInicio = this.rango?.start
      ? new Date(this.rango.start).toISOString().slice(0, 10).replace(/-/g, '')
      : '';
    const fechaFin = this.rango?.end
      ? new Date(this.rango.end).toISOString().slice(0, 10).replace(/-/g, '')
      : '';
    const nombreArchivo = `${nombreLuminaria}_${fechaInicio}_${fechaFin}.xlsx`;

    FileSaver.saveAs(blob, nombreArchivo);
  }

  private _getPastelPrimaryRgb(): string {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--theme-primary-500')
      .trim();
    let r = 0,
      g = 0,
      b = 0;
    const hexMatch = raw.match(/^#?([0-9a-fA-F]{6})$/);
    if (hexMatch) {
      r = parseInt(hexMatch[1].slice(0, 2), 16);
      g = parseInt(hexMatch[1].slice(2, 4), 16);
      b = parseInt(hexMatch[1].slice(4, 6), 16);
    } else {
      const rgbMatch = raw.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        r = +rgbMatch[1];
        g = +rgbMatch[2];
        b = +rgbMatch[3];
      }
    }
    const mix = (c: number) => Math.round(c * 0.25 + 255 * 0.75);
    return [mix(r), mix(g), mix(b)]
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }

  private _applyHeaderStyle(ws: XLSX.WorkSheet): void {
    const pastelRgb = this._getPastelPrimaryRgb();
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    for (let col = range.s.c; col <= range.e.c; col++) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[addr]) continue;
      ws[addr].s = {
        fill: { patternType: 'solid', fgColor: { rgb: pastelRgb } },
        font: { bold: true },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
    }
  }

  private _autoFitColumns(ws: XLSX.WorkSheet): void {
    const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
    if (!data.length) return;
    const colCount = data.reduce((max, row) => Math.max(max, row.length), 0);
    const widths: { wch: number }[] = [];
    for (let col = 0; col < colCount; col++) {
      const maxLen = data.reduce((max, row) => {
        const cell = row[col];
        return Math.max(max, cell != null ? String(cell).length : 0);
      }, 0);
      widths.push({ wch: Math.min(Math.max(maxLen + 2, 10), 60) });
    }
    ws['!cols'] = widths;
  }

  cerrar() {
    this.dialogRef.close();
  }
}
